import { useCallback, useEffect, useRef, useState } from 'react';
import type { RegistryCompanySuggestion } from '../types/company';
import { handleApiError, searchCompanyRegistry } from '../utils/api';
import { __ } from '@/hub-shared/utils/i18n';

const MIN_NAME_QUERY_LENGTH = 2;
const MIN_SIREN_QUERY_LENGTH = 9;
const DEBOUNCE_MS = 350;

export type RegistrySearchField = 'name' | 'siren';

export const formatRegistryOptionLabel = (row: RegistryCompanySuggestion): string => {
  const town = row.headquarters_town?.trim();
  const location = town ? ` — ${town}` : '';
  const closed = row.administrative_state === 'F' ? ` (${__('Cessée')})` : '';
  return `${row.name} — ${row.siren}${location}${closed}`;
};

export const buildRegistrySearchQuery = (
  name: string,
  siren: string,
  lastField: RegistrySearchField,
): string => {
  const sirenDigits = siren.replace(/\D/g, '');
  if (lastField === 'siren') {
    return sirenDigits.length === MIN_SIREN_QUERY_LENGTH ? sirenDigits : '';
  }
  const trimmedName = name.trim();
  if (trimmedName.length >= MIN_NAME_QUERY_LENGTH) {
    return trimmedName;
  }
  if (sirenDigits.length === MIN_SIREN_QUERY_LENGTH) {
    return sirenDigits;
  }
  return '';
};

export const useCompanyRegistrySearch = (
  bootstrapKey: string,
  enabled: boolean,
  name: string,
  siren: string,
  lastField: RegistrySearchField,
) => {
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState<RegistryCompanySuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  const runSearch = useCallback(
    async (query: string) => {
      const trimmed = query.trim();
      if (trimmed.length < MIN_NAME_QUERY_LENGTH) {
        setResults([]);
        setError(null);
        setLoading(false);
        setHasSearched(false);
        return;
      }

      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      setLoading(true);
      setError(null);
      try {
        const data = await searchCompanyRegistry(trimmed, bootstrapKey, controller.signal);
        if (controller.signal.aborted) {
          return;
        }
        setResults(data.results);
        setHasSearched(true);
      } catch (err) {
        if (controller.signal.aborted) {
          return;
        }
        setResults([]);
        setError(handleApiError(err));
        setHasSearched(true);
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    },
    [bootstrapKey],
  );

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    const query = buildRegistrySearchQuery(name, siren, lastField);
    if (!open && query.length < MIN_NAME_QUERY_LENGTH) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      void runSearch(query);
    }, DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timer);
    };
  }, [enabled, name, siren, lastField, open, runSearch]);

  useEffect(
    () => () => {
      abortRef.current?.abort();
    },
    [],
  );

  const close = useCallback(() => {
    setOpen(false);
    setResults([]);
    setError(null);
    setHasSearched(false);
  }, []);

  return {
    open,
    setOpen,
    results,
    loading,
    error,
    hasSearched,
    close,
  };
};
